import { Text, View } from "@react-pdf/renderer";
import CfmReportStyles from "../../CfmReportStyles";
import { formatCurrency } from "../../Utils/formatCurrency";

const CombinedDetailsSection = ({
  state,
  personInvestments = [],
  drawdowns = [],
  adHoc = [],
  primaryColor = "081e56",
  personPensions = [],
  personRegularIncome = [],
  personIncome = [],
  pensionResults = [],
}) => {
  const styles = CfmReportStyles(primaryColor);
  const name = state?.name || "The client";
  const pensions = personPensions || [];

  const getTfcAmount = (pensionName, age, pensionType) => {
    if (!pensionName || age === undefined || !pensionType) {
      // console.warn("getTfcAmount: Missing parameters", {
      //   pensionName,
      //   age,
      //   pensionType,
      // });
      return 0;
    }

    // Find matching pension entry
    const entry = pensionResults.find(
      (res) => res.name === pensionName && res.type === pensionType
    );

    if (!entry || !Array.isArray(entry.pensionFunds)) {
      // console.warn("getTfcAmount: No pension result entry found", {
      //   pensionName,
      //   pensionType,
      // });
      return 0;
    }

    // Find fund for matching age
    const fund = entry.pensionFunds.find((f) => Number(f.age) === Number(age));

    if (!fund) {
      // console.warn("getTfcAmount: No fund found for age", {
      //   pensionName,
      //   pensionType,
      //   age,
      //   availableAges: entry.pensionFunds.map((f) => f.age),
      // });
      return 0;
    }

    // console.log(fund.tflsWithdrawal, "TFC Withdrawal Amount");
    return fund.tflsWithdrawal || 0;
  };

  // Build a paragraph per pension
  const pensionParagraphs = pensions.map((pension) => {
    const fund = formatCurrency(pension.fundValue?.value);
    const contribution = formatCurrency(pension.contribution?.value);
    const contributionUnit =
      pension.contribution?.unit === "Annual" ? "per annum" : "per month";
    const growth = pension.atr || "N/A";
    const pensionName = pension.type || "Unnamed Pension";

    const taxedDraws = (pension.drawdowns || [])
      .map((dd) =>
        dd.unit === "Percentage %"
          ? `${formatCurrency(dd.value).slice(1)}% from age ${dd.start} to ${
              dd.end
            }`
          : `${formatCurrency(dd.value)} per annum from age ${dd.start} to ${
              dd.end
            }`
      )
      .join(", ");

    const tfcDraws = drawdowns.find(
      (d) => d.name === pension.name && d.type === pension.type
    );

    const tfcBits = [];

    // One-off TFC sentence
    if (tfcDraws?.age && tfcDraws?.percentage) {
      const tfcAmt = formatCurrency(
        getTfcAmount(pension.name, tfcDraws.age, pension.type)
      );
      tfcBits.push(
        `A one-off tax-free lump sum of ${tfcAmt} is taken at age ${tfcDraws.age} (${tfcDraws.percentage}% of the fund value, within HMRC's 25% allowance).`
      );
    }

    // Phased TFC sentence (sum and date span)
    if (Array.isArray(tfcDraws?.phases) && tfcDraws.phases.length) {
      const totalPhased = tfcDraws.phases.reduce(
        (sum, ph) => sum + (parseFloat(ph.amount) || 0),
        0
      );
      const phasedStart = tfcDraws.phases[0]?.start || tfcDraws.phases[0]?.date;
      const phasedEnd =
        tfcDraws.phases[tfcDraws.phases.length - 1]?.end ||
        tfcDraws.phases[tfcDraws.phases.length - 1]?.date;

      tfcBits.push(
        `Phased tax-free cash of ${formatCurrency(
          totalPhased
        )} is distributed between ${phasedStart} and ${phasedEnd}.`
      );
    }

    // Ad-hoc logic
    const pensionAdHocs = adHoc;
    // console.log("Pension AdHocs:", pensionAdHocs);

    const adHocContributions = pensionAdHocs
      .filter((item) => item.type === pensionName)
      .filter((item) => item.adHocType === "Contribution")
      .map(
        (item) =>
          `${formatCurrency(item.amount?.value)} contribution in ${item.year}`
      );

    const adHocWithdrawals = pensionAdHocs
      .filter((item) => item.type === pensionName)
      .filter((item) => item.adHocType !== "Contribution")
      .map(
        (item) =>
          `${formatCurrency(item.amount?.value)} withdrawal in ${item.year}`
      );

    let adHocText = "";
    if (adHocContributions.length > 0) {
      adHocText += ` ${adHocContributions.join(", ")}.`;
    }
    if (adHocWithdrawals.length > 0) {
      adHocText += `${adHocWithdrawals.join(", ")}.`;
    }

    // Final combined paragraph
    return `${name} holds a ${pensionName} pension with a fund value of ${fund}, contribution of ${contribution} ${contributionUnit} and a projected growth rate of ${growth}% based on Attitude to Risk. ${
      taxedDraws ? `Drawdowns are scheduled at ${taxedDraws}. ` : ""
    }${tfcBits.length ? ` ${tfcBits.join(" ")}` : ""}${
      adHocText ? ` Ad hoc activity includes a ${adHocText}` : ""
    }`;
  });

  const investmentParagraphs = personInvestments.map((investment) => {
    const fund = formatCurrency(investment.fundValue?.value);
    const contribution = formatCurrency(investment.contribution?.value);
    const contributionUnit =
      investment.contribution?.unit === "Annual" ? "per annum" : "per month";
    const startDate = investment.startDate
      ? ` from ${investment.startDate}`
      : "";

    const contStartDate = investment.contStartDate
      ? ` from age ${investment.contStartDate}`
      : "";
    const contEndDate = investment.contEndDate
      ? ` to ${investment.contEndDate}`
      : "";
    const growth = investment.atr || "";
    const investmentName = investment.type || "";
    // console.log(
    //   personRegularIncome,
    //   "Person Regular Income in CombinedDetailsSection"
    // );
    const matchedIncome = personRegularIncome.filter(
      (income) => income.type === investmentName && income.name === name
    );

    // console.log("Matched Income:", matchedIncome);

    // Ad-hoc logic
    const investmentAdHocs = adHoc;
    const adHocContributions = investmentAdHocs
      .filter((item) => item.type === investmentName)
      .filter((item) => item.adHocType === "Contribution")
      .map(
        (item) =>
          `${formatCurrency(item.amount?.value)} contribution in ${item.year}`
      );
    const adHocWithdrawals = investmentAdHocs
      .filter((item) => item.type === investmentName)
      .filter((item) => item.adHocType !== "Contribution")
      .map(
        (item) =>
          `${formatCurrency(item.amount?.value)} withdrawal in ${item.year}`
      );
    let adHocText = "";
    if (adHocContributions.length > 0) {
      adHocText += ` ${adHocContributions.join(",")}`;
    }
    if (adHocWithdrawals.length > 0) {
      adHocText += `${
        adHocContributions.length > 0 ? ", " : ""
      } ${adHocWithdrawals.join(", ")}`;
    }

    let incomeText = "";
    if (matchedIncome.length > 0) {
      incomeText += `Investment income of ${matchedIncome
        .map(
          (income) =>
            `${formatCurrency(income.amount)} expected from age ${
              income.start
            } to ${income.end}`
        )
        .join(", ")}.`;
    }
    //  Final combined paragraph
    // Here we combine all the details into a single paragraph
    return `${name}'s ${
      investmentName === "ISA"
        ? "Individual Savings Account (ISA)"
        : "General Investment Account (GIA)"
    } has a fund value of ${fund} starting ${startDate}, with a contribution of ${contribution} ${contributionUnit}${contStartDate} ${contEndDate} and a projected growth rate of ${growth}% based on Attitude to Risk. ${incomeText} ${
      adHocText ? `Ad hoc activity includes a ${adHocText}.` : ""
    }`;
  });

  const incomeParagraphs = personIncome.map((income) => {
    const amount = formatCurrency(income.amount?.value);
    const startDate = income.startDate ? ` from ${income.startDate}` : "";
    const endDate = income.endDate ? ` to ${income.endDate}` : "";
    return `${name} has a ${income.type} providing ${amount} per annum between ages${startDate} and ${endDate}.`;
  });

  return (
    <View style={styles.section}>
      <Text style={styles.disclaimerText1}>
        This section outlines your financial details for the scenario being
        modelled.
      </Text>
      {pensions.length > 0 && (
        <View style={styles.column}>
          <Text style={styles.subTitle}>
            Pension Summary (
            {pensions
              .map((p) => p.type)
              .filter(Boolean)
              .join(", ") || ""}
            )
          </Text>
          {pensionParagraphs.map((para, i) => (
            <Text
              key={i}
              style={{
                ...styles.text,
                textAlign: "justify",
              }}
            >
              {para}
            </Text>
          ))}
        </View>
      )}

      {personInvestments.length > 0 && (
        <View style={styles.column}>
          <Text style={styles.subTitle}>
            Investment Summary (
            {personInvestments
              .map((i) => i.type)
              .filter(Boolean)
              .join(", ") || ""}
            )
          </Text>
          {investmentParagraphs.map((para, i) => (
            <Text
              key={i}
              style={{
                ...styles.text,
                textAlign: "justify",
              }}
            >
              {para}
            </Text>
          ))}
        </View>
      )}
      {personIncome.length > 0 && (
        <View style={styles.column}>
          <Text style={styles.subTitle}>Income Summary</Text>
          {personIncome.length > 0 && (
            <Text
              style={{
                ...styles.text,
                // marginTop: 5,
                // marginBottom: 15,
                textAlign: "justify",
              }}
            >
              {name} has{" "}
              {personIncome
                .map((income) => {
                  const amount = formatCurrency(income.amount?.value);
                  const start = income.start;
                  const end = income.end;
                  return `a ${income.type} providing ${amount} per annum between ages ${start} and ${end}`;
                })
                .join(", ")}
              .
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

export default CombinedDetailsSection;
